import { useEffect, useCallback } from 'react';

type ModifierKey = 'ctrl' | 'alt' | 'shift' | 'meta';

interface ShortcutConfig {
  key: string;
  modifiers?: ModifierKey[];
  handler: () => void;
  preventDefault?: boolean;
}

/**
 * Hook to register keyboard shortcuts
 * @param shortcuts Array of shortcut configurations
 * @param enabled Whether shortcuts are enabled (default true)
 */
export function useKeyboardShortcuts(
  shortcuts: ShortcutConfig[],
  enabled: boolean = true
): void {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      // Don't trigger shortcuts when typing in inputs/textareas
      const target = event.target as HTMLElement;
      const isInputFocused =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;

      for (const shortcut of shortcuts) {
        const keyMatches = event.key.toLowerCase() === shortcut.key.toLowerCase();
        if (!keyMatches) continue;

        const modifiers = shortcut.modifiers || [];
        const ctrlMatch = modifiers.includes('ctrl') === (event.ctrlKey || event.metaKey);
        const altMatch = modifiers.includes('alt') === event.altKey;
        const shiftMatch = modifiers.includes('shift') === event.shiftKey;

        // For shortcuts with modifiers, allow even when in inputs
        // For shortcuts without modifiers, skip if in input
        if (!modifiers.length && isInputFocused) continue;

        if (ctrlMatch && altMatch && shiftMatch) {
          if (shortcut.preventDefault !== false) {
            event.preventDefault();
          }
          shortcut.handler();
          return;
        }
      }
    },
    [shortcuts, enabled]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
}

/**
 * Hook for a single keyboard shortcut
 */
export function useKeyboardShortcut(
  key: string,
  handler: () => void,
  modifiers: ModifierKey[] = [],
  enabled: boolean = true
): void {
  useKeyboardShortcuts(
    [{ key, modifiers, handler }],
    enabled
  );
}

export default useKeyboardShortcuts;
